// 📁 src/components/ZenPost.jsx
import { Link, useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { FaCode } from "react-icons/fa";
import { MonoButton } from "../kits";
import { motion } from "framer-motion/client";
import { PaperBG, PaperDarkBG } from "../kits";
import CollapsibleCodeBlock_screen from "../components/CollapsibleCodeBlock_screen";
import Preloader from "./Preloader";
import RoughBorder from "../kits/LayoutKit/RoughBorder";
import ReactMarkdown from "react-markdown";

// andere Beiträge anzeigen

import ZenListZen from "./ZenListZen";

function insertLineBreaks(text, wordsPerLine = 2) {
  if (!text) return null;
  const words = text.split(" ");
  const lines = [];
  for (let i = 0; i < words.length; i += wordsPerLine) {
    lines.push(words.slice(i, i + wordsPerLine).join(" "));
  }
  return lines.map((line, idx) => (
    <span key={idx}>
      {line}
      <br />
    </span>
  ));
}

// Markdown-Renderer-Komponente mit ZenPost-Styling
function MarkdownContent({ markdown }) {
  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        components={{
          h1: ({node, ...props}) => (
            <PaperDarkBG className="mt-8 mb-4 inline-block sm:block w-full rounded-lg px-4 py-2 relative -top-1">
              <h1 className="flex items-center gap-2 font-mono text-sm text-shadow text-amber-100" {...props}>
                <FaCode className="text-brand flex-shrink-0" />
                {props.children}
              </h1>
            </PaperDarkBG>
          ),
          h2: ({node, ...props}) => (
            <PaperDarkBG className="mt-8 mb-4 inline-block sm:block w-full rounded-lg px-4 py-2 relative -top-1">
              <h2 className="flex items-center gap-2 font-mono text-sm text-shadow text-amber-100" {...props}>
                <FaCode className="text-brand flex-shrink-0" />
                {props.children}
              </h2>
            </PaperDarkBG>
          ),
          h3: ({node, ...props}) => (
            <PaperDarkBG className="mt-8 mb-4 inline-block sm:block w-full rounded-lg px-4 py-2 relative -top-1">
              <h3 className="flex items-center gap-2 font-mono text-sm text-shadow text-amber-100" {...props}>
                <FaCode className="text-brand flex-shrink-0" />
                {props.children}
              </h3>
            </PaperDarkBG>
          ),
          p: ({node, ...props}) => (
            <p className="mb-4 ml-4 leading-relaxed font-mono text-xs sm:text-sm" {...props} />
          ),
          ul: ({node, ...props}) => (
            <ul className="list-disc ml-8 mb-4 text-xs sm:text-sm" {...props} />
          ),
          ol: ({node, ...props}) => (
            <ol className="list-decimal ml-6 mb-4 font-mono" {...props} />
          ),
          li: ({node, ...props}) => (
            <li className="font-mono text-xs" {...props} />
          ),
          blockquote: ({node, ...props}) => (
            <blockquote className="border-l-4 border-brand pl-4 pr-2 py-4 italic mb-4 text-xs text-brand font-mono mt-4 bg-gray-950" {...props} />
          ),
          code: ({node, inline, className, children, ...props}) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            if (!inline && language) {
              return (
                <CollapsibleCodeBlock_screen
                  code={String(children).replace(/\n$/, '')}
                  language={language}
                />
              );
            }
            return inline
              ? <code className="bg-gray-100 px-1 rounded text-xs" {...props}>{children}</code>
              : <code className="block bg-gray-100 p-2 rounded my-2 overflow-x-auto text-xs" {...props}>{children}</code>;
          },
          pre: ({node, ...props}) => (
            <pre className="bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7] p-2 rounded my-2 overflow-x-auto text-xs font-mono" {...props} />
          ),
          img: ({node, ...props}) => (
            <div className="my-6 p-4 bg-gradient-to-br from-amber-100 px-4 py-4 shadow-lg relative -top-1">
              <img className="w-full object-cover rounded-md" {...props} />
              {props.alt && (
                <p className="text-xs text-gray-600 mt-2 font-mono">{props.alt}</p>
              )}
            </div>
          ),
          hr: ({node, ...props}) => (
            <hr className="my-8 border-t-2 border-gray-300" {...props} />
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

export default function ZenPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ref für das große Bild oben
  const mainImageRef = useRef(null);

  // Array-Ref für alle Content-Bilder
  const blockImageRefs = useRef([]);

  useEffect(() => {
    if (!slug) return;
    fetch(`https://denisbitter.de/stage01/api/zens.php?slug=${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error("Fetch-Error");
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          setPost(data);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Preloader />;
  if (!post) return <div className="text-center text-sm text-gray-800 ">Kein Eintrag gefunden.</div>;

  return (
    <PaperBG className="sm:py-10">
      <div className="max-w-4xl w-full mx-auto pt-10 px-4 font-mono">
        {/* GROSSE Bildschirme */}
        <PaperDarkBG className="rounded-lg hidden sm:block">
          <div className="max-w-4xl mx-auto px-5 py-2 rounded-lg">
            <nav className="text-xs text-amber-100 mb-0 font-mono">
              <Link to="/lernstudio" className="hover:text-brand text-amber-100">LernStudio</Link>
              {" / "}
              <Link to="/lernstudio?tab=zendev" className="hover:text-brand text-amber-100">zendev</Link>
              {" / "}
              <span className="text-brand">{post.title}</span>
            </nav>
          </div>
        </PaperDarkBG>

        {/* KLEINE Bildschirme */}
        <div className="max-w-4xl mx-auto px-5 py-2 rounded-lg sm:hidden">
          <nav className="text-xs text-gray-900 mb-0 font-mono">
            <Link to="/lernstudio" className="hover:text-brand text-gray-900">LernStudio</Link>
            {" / "}
            <Link to="/lernstudio?tab=zendev" className="hover:text-white text-brand py-1 px-1 pt-0 pb-0  bg-gray-950 text-shadow">zendev</Link>
            {" / "}
            <span className="text-gray-950">{post.title}</span>
          </nav>
        </div>
      </div>

      <article className="max-w-4xl w-full mx-auto my-10 px-4">
        <PaperDarkBG className="rounded-lg">
          <div className="py-1">
            <div className="inline-block px-4 py-1 shadow-lg relative -top-1 text-shadow grid grid-cols-[auto_1fr] gap-x-2 items-start">
              {/* 1) Icon */}
              <FaCode className="mt-2 text-brand" />
              {/* 2) Title + Subtitle-Stack */}
              <motion className="grid grid-rows-[auto_auto] gap-y-0">
                <h1 className="font-mono text-sm mt-1 text-amber-100 ">{post.title}</h1>
              </motion>
            </div>
          </div>
        </PaperDarkBG>

        <div className="rounded-lg mt-2 ml-2">
          <div>
            {post.subtitle && (
              <h2 className="font-mono text-xs sm:text-sm mt-3 text-amber-100 ">
                {insertLineBreaks(post.subtitle, 5)}
              </h2>
            )}
          </div>
        </div>

        <div className="mt-9"></div>

        {/* Hauptbild mit RoughBorder */}
        {post.image && (
          <div
            ref={mainImageRef}
            className="block w-full md:inline-block md:max-w-prose 
            bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]  px-4 py-4 shadow-lg relative -top-1"
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full object-cover rounded-md"
            />
            <RoughBorder
              parentRef={mainImageRef}
              radius={10}
              padding={2}
          color="#AC8E66" 
              strokeWidth={0.3}
              roughness={1}
              bowing={2}
            />
          </div>
        )}

        {/* Content - Markdown oder Blocks */}
        {post.markdown ? (
          // Markdown-Rendering (neue Posts)
          <MarkdownContent markdown={post.markdown} />
        ) : (
          // JSON-Blocks-Rendering (alte Posts, Fallback)
          post.blocks && post.blocks.map((block, i) => {
          switch (block.type) {
            case "header":
              const Tag = `h${block.data.level}`;
              return (
                <PaperDarkBG key={i}
                  className="mt-8 mb-4 inline-block sm:block w-full rounded-lg px-4 py-2 relative -top-1"
                >
                <Tag className="flex items-center gap-2 font-mono text-sm text-shadow text-amber-100">
                    <FaCode className="text-brand flex-shrink-0" />
                    <span>{block.data.text}</span>
                  </Tag>
                </PaperDarkBG>
              );
            case "paragraph":
              return (
                <p key={i} className="mb-4 ml-4 leading-relaxed font-mono text-xs sm:text-sm">
                  {block.data.text}
                </p>
              );
            case "list": {
              const items = block.data.items;
              if (!Array.isArray(items) || items.length === 0) return null;
              if (typeof items[0] === "string") {
                if (block.data.style === "unordered") {
                  return (
                    <ul key={i} className="list-disc ml-8 mb-4  text-xs sm:text-sm">
                      {items.map((textItem, idx) => (
                        <li key={idx}>{textItem}</li>
                      ))}
                    </ul>
                  );
                } else {
                  return (
                    <ol key={i} className="list-decimal ml-6 mb-4">
                      {items.map((textItem, idx) => (
                        <li className="font-mono" key={idx}>{textItem}</li>
                      ))}
                    </ol>
                  );
                }
              }
              if (typeof items[0] === "object") {
                if (block.data.style === "unordered") {
                  return (
                    <ul key={i} className="list-disc ml-6 mb-4">
                      {items.map((itemObj, idx) => {
                        const text = itemObj.content ?? itemObj.text ?? "(kein Text)";
                        return <li className="font-mono text-xs" key={idx}>{text}</li>;
                      })}
                    </ul>
                  );
                } else {
                  return (
                    <ol key={i} className="list-decimal ml-6 mb-4">
                      {items.map((itemObj, idx) => {
                        const text = itemObj.content ?? itemObj.text ?? "(kein Text)";
                        return <li key={idx}>{text}</li>;
                      })}
                    </ol>
                  );
                }
              }
              return null;
            }
            case "quote":
              return (
                <blockquote key={i} className="
                border-l-4
                 border-brand 
                 pl-4 pr-2 py-4 italic mb-4 text-xs
                  text-brand font-mono mt-4 bg-gray-950">
                  <p>{block.data.text}</p>
                  {block.data.caption && (
                    <cite className="block text-right font-mono">
                      — {block.data.caption}
                    </cite>
                  )}
                </blockquote>
              );
            // Bild-Block mit RoughBorder + array-ref!
            case "image": {
              const src = block.data.file?.url || block.data.src;
              const alt = block.data.caption || block.data.alt || "Bild";
              if (!src) {
                return (
                  <div key={i} className="text-red-600 font-mono text-xs my-4">
                    ⚠️ Kein Bild gefunden im Block {i}
                    <pre>{JSON.stringify(block.data, null, 2)}</pre>
                  </div>
                );
              }
              return (
                <div
                  key={i}
                  ref={el => blockImageRefs.current[i] = el}
                  className="my-6 p-4 
                  bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]
                  px-4 py-4 shadow-lg relative -top-1"
                >
                  <img
                    src={src}
                    alt={alt}
                    className="w-full max-w-xl mx-auto rounded-md shadow-inherit"
                  />
                  {alt && (
                    <figcaption className="mt-2 text-xs text-center text-gray-600 font-mono">
                      {alt}
                    </figcaption>
                  )}
                  <RoughBorder
                    parentRef={{ current: blockImageRefs.current[i] }}
                    radius={10}
                    padding={2}
                   color="#AC8E66" 
                    strokeWidth={1.1}
                    roughness={2}
                    bowing={2}
                  />
                </div>
              );
            }
            case "code":
              return (
                <CollapsibleCodeBlock_screen code={block.data.code} language={block.data.language} key={i} />
              );
            case "delimiter":
              return <hr key={i} className="my-8 border-brand" />;
            case "embed":
              return (
                <div key={i} className="my-6 aspect-w-16 aspect-h-9">
                  <iframe
                    src={block.data.embed}
                    title={block.data.caption || `Embed-${i}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              );
            case "checklist":
              return (
                <ul key={i} className="list-none ml-0 mb-4">
                  {block.data.items.map((item, idx) => (
                    <li key={idx} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={item.checked}
                        readOnly
                        className="mr-2"
                      />
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              );
            case "cta":
              return (
                <div key={i} className="my-6 text-center ">
                  <MonoButton
                    className="w-full  text-xs px-2 py-1 pt-2 pb-2 text-shadow"
                    textColor="text-amber-100"
                    underlineColors="ZenAmber"


                    onClick={() => {
                      const url = block.data.url || "#";
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                  >
                    {block.data.text || "Weiter"}
                  </MonoButton>
                </div>
              );
            case "table":
              return (
                <table key={i} className="table-auto border-collapse mb-6">
                  <tbody>
                    {block.data.content.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="border px-4 py-2 text-left"
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            case "youtube":
              // YouTube-Block: Extrahiere Video-ID und baue Embed-URL
              let videoId = block.data.videoId || block.data.id;

              // Wenn keine direkte ID vorhanden ist, parse die URL
              if (!videoId && block.data.url) {
                const url = block.data.url;
                // Für youtu.be/VIDEO_ID Format
                if (url.includes('youtu.be/')) {
                  videoId = url.split('youtu.be/')[1]?.split('?')[0]?.split('&')[0];
                }
                // Für youtube.com/watch?v=VIDEO_ID Format
                else if (url.includes('v=')) {
                  videoId = url.split('v=')[1]?.split('&')[0];
                }
              }

              if (!videoId) {
                return (
                  <div key={i} className="text-red-600 font-mono text-xs my-4">
                    ⚠️ Keine YouTube-Video-ID gefunden
                  </div>
                );
              }
              return (
                <div key={i} className="my-6 aspect-w-16 aspect-h-9">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={block.data.caption || `YouTube Video ${i}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full rounded-md"
                    style={{ aspectRatio: '16/9', minHeight: '315px' }}
                  />
                  {block.data.caption && (
                    <p className="text-xs text-gray-600 mt-2 font-mono text-center">
                      {block.data.caption}
                    </p>
                  )}
                </div>
              );
            default:
              return (
                <div key={i} className="mb-4 p-4 bg-brand border-l-4 border-brand">
                  <p className="text-sm text-yellow-800">
                    (Dieser Block‐Typ &quot;{block.type}&quot; wird noch nicht gerendert.)
                  </p>
                </div>


              );
          }
        })
        )}
        <ZenListZen currentSlug={slug} max={3} />
      </article>
    </PaperBG>
  );
}
